import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import type {
  DateRangeErrors,
  SelectedDateRange,
} from '../interfaces/Dashboard';

dayjs.extend(utc);

const API_DATE_FORMAT = 'YYYY-MM-DD HH:mm:ss';

export const emptyDateRangeErrors = (): DateRangeErrors => ({
  fromdate: { isError: false, errorMessage: '' },
  todate: { isError: false, errorMessage: '' },
});

/**
 * Local-time day boundaries N days back through end of today.
 * Deliberately local (not UTC) — this matches the legacy behaviour for the
 * Today / 4 Days / 7 Days presets.
 */
export const getDateRange = (daysToSubtract: number) => {
  const today = dayjs();
  return {
    startDate: today
      .subtract(daysToSubtract, 'day')
      .startOf('day')
      .format(API_DATE_FORMAT),
    endDate: today.endOf('day').format(API_DATE_FORMAT),
  };
};

/** UTC window ending now, going back `amount` of `unit`. */
const utcWindow = (amount: number, unit: 'minute' | 'hour') => ({
  startDate: dayjs.utc().subtract(amount, unit).format(API_DATE_FORMAT),
  endDate: dayjs.utc().format(API_DATE_FORMAT),
});

export interface ResolvedDateFilter {
  /** Sent to the API; `null` means "no bound". */
  fromdate: string | null;
  todate: string | null;
  /** Human-readable labels shown in the summary heading. */
  labels: { fromdate: string; todate: string };
}

/**
 * Turns the selected date-filter preset (plus any custom dates) into the
 * from/to values sent to the API and the labels shown in the heading.
 *
 * "All" sends no bounds and displays "Start"/"Present"; every other preset
 * resolves to a concrete window and is labelled with a " UTC" suffix.
 */
export const resolveDateFilter = (
  dateFilterValue: string,
  customFromDate: string,
  customToDate: string,
): ResolvedDateFilter => {
  let fromdate: string | null = null;
  let todate: string | null = null;

  switch (dateFilterValue) {
    case 'All':
      break;
    case '30_Min': {
      const window = utcWindow(30, 'minute');
      fromdate = window.startDate;
      todate = window.endDate;
      break;
    }
    case '60_Min': {
      const window = utcWindow(60, 'minute');
      fromdate = window.startDate;
      todate = window.endDate;
      break;
    }
    case '8_Hours': {
      const window = utcWindow(8, 'hour');
      fromdate = window.startDate;
      todate = window.endDate;
      break;
    }
    case '12_Hours': {
      const window = utcWindow(12, 'hour');
      fromdate = window.startDate;
      todate = window.endDate;
      break;
    }
    case 'Today': {
      const range = getDateRange(0);
      fromdate = range.startDate;
      todate = range.endDate;
      break;
    }
    case 'Four_Days': {
      const range = getDateRange(4);
      fromdate = range.startDate;
      todate = range.endDate;
      break;
    }
    case 'Seven_Days': {
      const range = getDateRange(7);
      fromdate = range.startDate;
      todate = range.endDate;
      break;
    }
    case 'Custom': {
      if (customFromDate) {
        fromdate = dayjs(customFromDate).startOf('day').format(API_DATE_FORMAT);
      }
      if (customToDate) {
        todate = dayjs(customToDate).endOf('day').format(API_DATE_FORMAT);
      }
      break;
    }
    default:
      break;
  }

  return {
    fromdate,
    todate,
    labels: {
      fromdate: fromdate ? `${fromdate} UTC` : 'Start',
      todate: todate ? `${todate} UTC` : 'Present',
    },
  };
};

/** Builds the heading descriptor from a resolved filter. */
export const toSelectedDateRange = (
  resolved: ResolvedDateFilter,
  filterType: string,
): SelectedDateRange => ({
  fromdate: resolved.labels.fromdate,
  todate: resolved.labels.todate,
  filterType,
});

/**
 * Validates a custom from/to pair: neither may be in the future, and from must
 * not be after to. Returns per-field errors for the inputs to render.
 */
export const validateDateRange = (
  fromValue: string,
  toValue: string,
): DateRangeErrors => {
  const errors = emptyDateRangeErrors();
  const now = dayjs();
  const from = fromValue ? dayjs(fromValue) : null;
  const to = toValue ? dayjs(toValue) : null;

  if (from?.isValid()) {
    if (from.isAfter(now)) {
      errors.fromdate = {
        isError: true,
        errorMessage: 'Date can not be in future.',
      };
    } else if (to?.isValid() && from.isAfter(to)) {
      errors.fromdate = {
        isError: true,
        errorMessage: 'Date can not be greater than to date.',
      };
    }
  }

  if (to?.isValid()) {
    if (to.isAfter(now)) {
      errors.todate = {
        isError: true,
        errorMessage: 'Date can not be in future.',
      };
    } else if (from?.isValid() && from.isAfter(to)) {
      errors.todate = {
        isError: true,
        errorMessage: 'Date can not be less than from date.',
      };
    }
  }

  return errors;
};

export const formatExportTimestamp = () => dayjs().format('YYYY-MM-DD');
